import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {IssueUnmarkAsDuplicate} from '@github-ui/server-persisted-queries'
import type {IssueUnmarkAsDuplicateMutation} from '@github-ui/server-persisted-queries/types/IssueUnmarkAsDuplicate'
import {type JSONRequestInit, verifiedFetchJSON} from '@github-ui/verified-fetch'

type UnmarkIssueAsDuplicateInput = {
  canonicalId: string
  duplicateId: string
}

type UnmarkIssueAsDuplicateVariables = {
  input: UnmarkIssueAsDuplicateInput
}

export function useUnmarkIssueAsDuplicateMutation({
  issueUrl,
  onError,
  onCompleted,
}: {
  issueUrl: string
  onError?: (error: Error) => void
  onCompleted?: (response: IssueUnmarkAsDuplicateMutation) => void
}): UseMutationResult<IssueUnmarkAsDuplicateMutation, Error, UnmarkIssueAsDuplicateVariables> {
  return useMutation<IssueUnmarkAsDuplicateMutation, Error, UnmarkIssueAsDuplicateVariables>({
    mutationFn: async ({input: {canonicalId, duplicateId}}) => {
      return submitUnmarkIssueAsDuplicate({issueUrl, canonicalId, duplicateId})
    },
    onError,
    onSuccess: onCompleted,
  })
}

async function submitUnmarkIssueAsDuplicate({
  issueUrl,
  canonicalId,
  duplicateId,
}: {
  issueUrl: string
  canonicalId: string
  duplicateId: string
}): Promise<IssueUnmarkAsDuplicateMutation> {
  const requestUrl = `${issueUrl.replace(/\/$/, '')}/unmark_duplicate`
  const body = {
    duplicateId,
    canonicalId,
    queryId: IssueUnmarkAsDuplicate,
  }

  const request: JSONRequestInit = {
    method: 'POST',
    body,
  }

  const response = await verifiedFetchJSON(requestUrl, request)
  if (!response.ok) {
    throw new Error(`Issue duplicate undo failed (${response.status})`)
  }

  const json = (await response.json()) as {
    data?: IssueUnmarkAsDuplicateMutation
    errors?: Array<{message?: string}>
  }

  if (!json.data) {
    const message = json.errors?.[0]?.message || 'Issue duplicate undo failed'
    throw new Error(message)
  }

  return json.data
}
